/**
 * Create Employee page — routed at ``/employees/new``.
 *
 * Background: the list page's "Add Employee" button navigates here, but
 * the route didn't exist before so react-router fell through to
 * ``/employees/:id`` and the profile page tried to ``GET /employees/new``
 * → "Failed to load employee" toast. This page fixes that by actually
 * handling the create flow: a minimal form that POSTs to ``/employees``
 * and then redirects to the new record's profile.
 */
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileSignature, Loader2, UserPlus } from "lucide-react";

import { createEmployee } from "@/api/employees";
import { listDepartments } from "@/api/organizations";
import { listEmployees } from "@/api/employees";
import {
  listConfigTemplates,
  type DocumentTemplateConfig,
} from "@/api/documentTemplates";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { pageTransition } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { useToast } from "@/hooks/useToast";
import type { DepartmentFull } from "@/types/organization";
import type {
  EmployeeCreateData,
  EmployeeListItem,
  EmployeeType,
} from "@/types/employee";
import type { EmploymentType } from "@/types/common";

const EMPLOYMENT_TYPES: { value: EmploymentType; label: string }[] = [
  { value: "FULL_TIME", label: "Full-time" },
  { value: "PART_TIME", label: "Part-time" },
  { value: "CONTRACTOR", label: "Contractor" },
  { value: "TEMP", label: "Temp" },
];

/**
 * Shape the axios/pydantic error into a single sentence the user can act
 * on. Validation errors arrive as ``detail: [{loc, msg}, …]``; plain
 * errors arrive as ``detail: "…"``. Anything else falls through to a
 * generic message.
 */
function humanizeCreateError(err: unknown): string {
  const resp = (err as { response?: { data?: { detail?: unknown } } })?.response;
  const detail = resp?.data?.detail;
  if (Array.isArray(detail)) {
    const first = detail[0] as { loc?: unknown[]; msg?: string } | undefined;
    if (first?.msg) {
      const field = String(first.loc?.[first.loc.length - 1] ?? "");
      return field ? `${field.replace(/_/g, " ")}: ${first.msg}` : first.msg;
    }
  }
  if (typeof detail === "string") return detail;
  return "Couldn't create the employee. Please try again.";
}

export function EmployeeCreatePage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { addToast } = useToast();

  const canCreate = can("manage_employees");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [title, setTitle] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [managerId, setManagerId] = useState("");
  const [hireDate, setHireDate] = useState(
    () => new Date().toISOString().slice(0, 10)
  );
  const [employmentType, setEmploymentType] = useState<EmploymentType>("FULL_TIME");
  const [createUser, setCreateUser] = useState(true);
  // Default to "new_hire" — matches the spec and the most common path.
  // Existing-employee migration is the deliberate opt-out.
  const [employeeType, setEmployeeType] = useState<EmployeeType>("new_hire");
  // Only meaningful for the existing path. Defaults off so a 200-row
  // migration import doesn't accidentally email everyone.
  const [sendInvite, setSendInvite] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [departments, setDepartments] = useState<DepartmentFull[]>([]);
  const [managers, setManagers] = useState<EmployeeListItem[]>([]);
  const [templates, setTemplates] = useState<DocumentTemplateConfig[]>([]);

  // Load departments, the first page of active employees (as the
  // manager pool), and the org's document templates in parallel on
  // mount. Failures are non-fatal — the form still submits; the selects
  // and the auto-assign preview just stay empty.
  useEffect(() => {
    if (!canCreate) return;
    let cancelled = false;
    Promise.all([
      listDepartments().catch(() => [] as DepartmentFull[]),
      listEmployees(1, 100, { status: "ACTIVE" }).catch(() => ({
        items: [] as EmployeeListItem[],
      })),
      listConfigTemplates().catch(() => [] as DocumentTemplateConfig[]),
    ]).then(([depts, page, tpls]) => {
      if (cancelled) return;
      setDepartments(depts as DepartmentFull[]);
      setManagers((page as { items: EmployeeListItem[] }).items);
      setTemplates(tpls as DocumentTemplateConfig[]);
    });
    return () => {
      cancelled = true;
    };
  }, [canCreate]);

  // Live preview of which templates this hire will receive on create —
  // mirrors ``document_template_service.applies_to_employee`` so HR sees
  // exactly what the backend will spawn. Recomputes whenever the form's
  // targeting inputs (department, employment type) move.
  const autoAssignPreview = useMemo(() => {
    if (templates.length === 0) return [] as DocumentTemplateConfig[];
    return templates.filter((t) => {
      if (!t.is_active) return false;
      if (t.applies_to_all) return true;
      if (
        departmentId &&
        Array.isArray(t.applies_to_departments) &&
        t.applies_to_departments.some((d) => String(d) === departmentId)
      ) {
        return true;
      }
      if (
        Array.isArray(t.applies_to_employment_types) &&
        t.applies_to_employment_types.some(
          (et) => String(et) === employmentType,
        )
      ) {
        return true;
      }
      return false;
    });
  }, [templates, departmentId, employmentType]);

  const managerOptions = useMemo(
    () =>
      managers.map((m) => ({
        value: m.id,
        label: `${m.first_name} ${m.last_name}${m.title ? ` — ${m.title}` : ""}`,
      })),
    [managers]
  );

  if (!canCreate) {
    return (
      <div className="py-12 text-center text-text-muted">
        You don't have permission to add employees.
      </div>
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (!firstName.trim() || !lastName.trim()) {
      setError("First and last name are required.");
      return;
    }
    if (!email.trim()) {
      setError("Work email is required.");
      return;
    }
    if (!title.trim()) {
      setError("Job title is required.");
      return;
    }

    const payload: EmployeeCreateData = {
      first_name: firstName.trim(),
      last_name: lastName.trim(),
      work_email: email.trim(),
      title: title.trim(),
      hire_date: hireDate,
      employment_type: employmentType,
      department_id: departmentId || undefined,
      manager_id: managerId || undefined,
      create_user_account: createUser,
      employee_type: employeeType,
      // Only forward send_login_invitation when the user is on the
      // existing path; new_hire always sends.
      send_login_invitation:
        employeeType === "existing" ? sendInvite : undefined,
    };

    setSubmitting(true);
    try {
      const created = await createEmployee(payload);
      addToast("success", `${created.first_name} ${created.last_name} added`);
      navigate(`/employees/${created.id}`, { replace: true });
    } catch (err) {
      setError(humanizeCreateError(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <motion.div {...pageTransition} className="mx-auto max-w-2xl">
      <button
        onClick={() => navigate("/employees")}
        className="flex items-center gap-1 text-sm text-text-muted hover:text-text-primary mb-4 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to People
      </button>

      <div className="mb-6">
        <h1 className="text-2xl font-semibold font-heading text-text-primary flex items-center gap-2">
          <UserPlus className="h-6 w-6 text-accent" />
          Add Employee
        </h1>
        <p className="text-sm text-text-muted mt-1">
          Create a new employee record. You can optionally send them a login
          invite right away.
        </p>
      </div>

      <Card className="p-6">
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          {/* Hire flow toggle — drives the entire create-time side
              effect graph. New Hire sends the welcome email + leaves
              onboarding "pending" so a manager can fire Pilot from
              the profile. Existing is the silent migration path. */}
          <div>
            <p className="text-xs font-medium uppercase tracking-wide text-text-muted mb-2">
              This employee is&hellip;
            </p>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
              <label
                className={`flex items-start gap-2 rounded-md border p-3 cursor-pointer transition-colors ${
                  employeeType === "new_hire"
                    ? "border-accent bg-accent/5"
                    : "border-border hover:bg-surface-hover"
                }`}
              >
                <input
                  type="radio"
                  name="employee_type"
                  className="mt-1 accent-accent"
                  checked={employeeType === "new_hire"}
                  onChange={() => setEmployeeType("new_hire")}
                />
                <div>
                  <div className="text-sm font-medium text-text-primary">
                    New Hire
                  </div>
                  <div className="text-xs text-text-muted mt-0.5">
                    Joining the company. Sends a welcome email; you can
                    start onboarding from their profile when ready.
                  </div>
                </div>
              </label>
              <label
                className={`flex items-start gap-2 rounded-md border p-3 cursor-pointer transition-colors ${
                  employeeType === "existing"
                    ? "border-accent bg-accent/5"
                    : "border-border hover:bg-surface-hover"
                }`}
              >
                <input
                  type="radio"
                  name="employee_type"
                  className="mt-1 accent-accent"
                  checked={employeeType === "existing"}
                  onChange={() => setEmployeeType("existing")}
                />
                <div>
                  <div className="text-sm font-medium text-text-primary">
                    Existing Employee
                  </div>
                  <div className="text-xs text-text-muted mt-0.5">
                    Already works here (migration). No emails, no
                    onboarding. You can upload signed documents later.
                  </div>
                </div>
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label="First name"
              required
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
            <Input
              label="Last name"
              required
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>

          <Input
            type="email"
            label="Work email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />

          <Input
            label="Job title"
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Select
              label="Department"
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              options={[
                { value: "", label: "— None —" },
                ...departments.map((d) => ({ value: d.id, label: d.name })),
              ]}
            />

            <Select
              label="Manager"
              value={managerId}
              onChange={(e) => setManagerId(e.target.value)}
              options={[{ value: "", label: "— None —" }, ...managerOptions]}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              type="date"
              label="Hire date"
              required
              value={hireDate}
              onChange={(e) => setHireDate(e.target.value)}
            />
            <Select
              label="Employment type"
              value={employmentType}
              onChange={(e) =>
                setEmploymentType(e.target.value as EmploymentType)
              }
              options={EMPLOYMENT_TYPES.map((t) => ({
                value: t.value,
                label: t.label,
              }))}
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-text-primary">
            <input
              type="checkbox"
              checked={createUser}
              onChange={(e) => setCreateUser(e.target.checked)}
              className="h-4 w-4 rounded border-border accent-accent"
            />
            {employeeType === "new_hire"
              ? "Create a user account and send an invitation email"
              : "Create a user account (no email sent unless you opt in below)"}
          </label>

          {/* Existing-employee opt-in for the welcome email. Only
              shown when the user picked the migration path AND chose
              to create a user account — outside that combination the
              control would be misleading. */}
          {employeeType === "existing" && createUser && (
            <label className="flex items-center gap-2 text-sm text-text-primary pl-6">
              <input
                type="checkbox"
                checked={sendInvite}
                onChange={(e) => setSendInvite(e.target.checked)}
                className="h-4 w-4 rounded border-border accent-accent"
              />
              Send login invitation now
            </label>
          )}

          {/* Auto-assign preview only makes sense for new hires —
              existing-employee creates skip Pilot + the document
              fan-out entirely until "Start Onboarding" is hit on
              the profile. */}
          {employeeType === "new_hire" && (
            <div className="rounded-md border border-border/60 bg-surface px-3 py-3">
              <div className="flex items-center gap-2 text-sm font-medium text-text-primary">
                <FileSignature className="h-4 w-4 text-accent" />
                Onboarding will start later
              </div>
              <p className="mt-1 text-xs text-text-muted">
                The welcome email goes out now. Documents and the
                Pilot checklist wait until you click&nbsp;
                <strong>Start Onboarding</strong> on their profile —
                that&rsquo;s when {autoAssignPreview.length || "the"}{" "}
                applicable template
                {autoAssignPreview.length === 1 ? "" : "s"} get assigned and
                their manager gets notified.
              </p>
              {autoAssignPreview.length > 0 && (
                <ul className="mt-2 space-y-1 text-xs text-text-muted">
                  {autoAssignPreview.slice(0, 5).map((t) => (
                    <li key={t.id} className="flex items-center gap-2">
                      <span className="h-1.5 w-1.5 rounded-full bg-accent/60" />
                      <span className="text-text-primary">{t.name}</span>
                      {t.requires_signature && (
                        <span className="text-[10px] uppercase tracking-wide text-accent">
                          Signature
                        </span>
                      )}
                    </li>
                  ))}
                  {autoAssignPreview.length > 5 && (
                    <li className="italic">
                      + {autoAssignPreview.length - 5} more
                    </li>
                  )}
                </ul>
              )}
            </div>
          )}

          {error && (
            <div
              role="alert"
              className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger"
            >
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate("/employees")}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating…
                </>
              ) : (
                "Create employee"
              )}
            </Button>
          </div>
        </form>
      </Card>
    </motion.div>
  );
}

export default EmployeeCreatePage;
